import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useContext,
  FC,
  Dispatch,
  SetStateAction,
} from "react";
import { Storage, Store } from "../utils/Storage";
import { CircumstancesType } from "../constants";
import {
  IProfileForm,
  defaultProfileFormValues,
} from "../screens/Profile/model";
import { FileData } from "../screens/WalletScreen/WalletScreen";

export interface ErrorItem {
  key: string;
  message: string;
  tab: string;
}


interface IAppContext {
  data: Partial<Store>;
  mode: CircumstancesType[];
  profileFormDataContext: IProfileForm;
  passportFile: FileData | null;
  saveStatus: boolean;
  setSaveStatus: Dispatch<SetStateAction<boolean>>;
  authUpdate: boolean;
  setAuthUpdate: Dispatch<SetStateAction<boolean>>;
  setData: Dispatch<SetStateAction<Store | undefined>>;
  setMode: (circumstancesType: CircumstancesType[]) => Promise<void>;
  setProfileFormDataContext: (data: IProfileForm) => void;
  setPassportFile: (data: FileData) => void;
  errorItems: ErrorItem[]; // New property for ErrorItem[]
  setErrorItems: Dispatch<SetStateAction<ErrorItem[]>>; // Updater for ErrorItem[]
}

const AppContext = createContext<IAppContext>({
  data: {},
  mode: [],
  profileFormDataContext: defaultProfileFormValues,
  passportFile: null,
  saveStatus: true,
  setSaveStatus: () => {},
  authUpdate: false,
  setAuthUpdate: () => {},
  setMode: async () => {},
  setData: () => {},
  setProfileFormDataContext: () => {},
  setPassportFile: () => {},
  errorItems: [], // Default value for ErrorItem[]
  setErrorItems: () => {}, // Default noop function
});

const AppProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [data, setData] = useState<Store>();
  const [mode, setMode] = useState<CircumstancesType[]>([]);
  const [profileFormDataContext, setProfileFormDataContext] =
      useState<IProfileForm>();
  const [passportFile, setPassportFile] = useState<FileData | null>(null);
  const [saveStatus, setSaveStatus] = useState<boolean>(true);
  const [authUpdate, setAuthUpdate] = useState<boolean>(false);
  const [errorItems, setErrorItems] = useState<ErrorItem[]>([]); // State for ErrorItem[]

  const storedDataKey = "custom:circumstances";

  const initCircumstancesMode = useCallback(async (): Promise<void> => {
    const curcumstancesValue =
        ((await Storage.getValue(storedDataKey)) as string) ?? "";
    const circumstancesObjectMap = curcumstancesValue
        .split(",")
        .filter((val) => !!val) as CircumstancesType[];
    setMode(circumstancesObjectMap);
  }, []);

  const getStorageValues = useCallback(async () => {
    const _data = await Storage.getStorage();
    setData(_data as Store);
  }, []);

  const setModeForCircumstancesType = async (
      circumstances: CircumstancesType[]
  ) => {
    await Storage.setValue(storedDataKey, circumstances.join(","));
    setMode(circumstances);
  };

  useEffect(() => {
    initCircumstancesMode();
    getStorageValues();
  }, [getStorageValues]);

  return (
      <AppContext.Provider
          value={{
            data: data as Store,
            mode,
            setMode: setModeForCircumstancesType,
            setData,
            profileFormDataContext: profileFormDataContext as IProfileForm,
            passportFile,
            setProfileFormDataContext,
            setPassportFile,
            saveStatus,
            setSaveStatus,
            authUpdate,
            setAuthUpdate,
            errorItems, // Providing ErrorItem[] in context
            setErrorItems, // Providing setErrorItems in context
          }}
      >
        {children}
      </AppContext.Provider>
  );
};

export default AppProvider;

export const useAppContext = () => {
  const context = useContext(AppContext);
  return context;
};
